import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Empty,
  Table,
  Button,
  Space,
  Tag,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  Typography,
  Tooltip,
  Badge,
  Modal,
  Form,
  Descriptions,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import apiService from "services/api/apiService";
import { Tabs } from 'antd';
import { SettingOutlined, BankOutlined, GlobalOutlined } from '@ant-design/icons';
import LeaveRequestForm from '../components/Leave/LeaveRequestForm';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const LeaveManagement: React.FC = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingRequest, setEditingRequest] = useState<any>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState<string>('all');
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [rejectionForm] = Form.useForm();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [leaveTab, setLeaveTab] = useState('requests');
  const [balances, setBalances] = useState<any[]>([]);
  const [calendar, setCalendar] = useState<any[]>([]);
  const [holidays, setHolidays] = useState<any[]>([]);
  const [entitlements, setEntitlements] = useState<any[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date().toISOString().slice(0,7));
  const [holidayModal, setHolidayModal] = useState(false);
  const [holidayForm] = Form.useForm();
  // const [refreshKey, setRefreshKey] = useState(0); // REMOVED - was causing infinite loop

  // Memoized fetch function to prevent infinite loops
  const fetchLeaveData = useCallback(async (showMessage = false) => {
  try {
    setLoading(true);
    console.log("🔄 Fetching leave data...");
    
    const response = await apiService.getLeaveRequests();
    console.log('📥 Raw API Response:', response);
    
    if (response?.success) {
      // The API returns {success: true, data: [...]} where data is the array directly
      let requestData = response.data || [];
      
      // Handle if response.data has nested structure
      if (!Array.isArray(requestData)) {
        if (requestData.data) {
          requestData = requestData.data;
        } else if (requestData.requests) {
          requestData = requestData.requests;
        } else {
          requestData = [];
        }
      }
      
      console.log('✅ Processed requests data:', requestData);
      console.log('📊 Total requests found:', requestData.length);
      
      // Ensure it's always an array
      const finalData = Array.isArray(requestData) ? requestData : [];
      setRequests(finalData);
      
      if (showMessage && finalData.length >= 0) {
        message.success(`Loaded ${finalData.length} leave requests`);
      }
    } else {
      console.error('❌ API Error:', response?.error);
      message.error(response?.error || 'Failed to fetch leave requests');
      setRequests([]);
    }
  } catch (error) {
    console.error('💥 Fetch Error:', error);
    message.error('Failed to connect to server');
    setRequests([]);
  } finally {
    setLoading(false);
  }
  }, []);
  const fetchLeaveExtras = async () => {
    try {
      const [br, cr, hr, er] = await Promise.all([
        apiService.getLeaveBalance(),
        apiService.getLeaveCalendar(calendarMonth),
        apiService.getPublicHolidays(),
        apiService.getLeaveEntitlements(),
      ]);
      if (br.success) setBalances(br.data || []);
      if (cr.success) setCalendar(cr.data || []);
      if (hr.success) setHolidays(hr.data || []);
      if (er.success) setEntitlements(er.data || []);
    } catch {}
  };

  useEffect(() => { if (leaveTab !== 'requests') fetchLeaveExtras(); }, [leaveTab, calendarMonth]); // FIXED: Empty dependencies to prevent infinite loop

  // Initial load
  useEffect(() => {
    fetchLeaveData(true);
  }, [fetchLeaveData]);

  // Force refresh function
  //   const forceRefresh = () => {
  //     console.log('🔄 Force refreshing data...');
  //     setRefreshKey(prev => prev + 1);
  //   };

  const handleView = (request: any) => {
    console.log('👁️ Viewing request:', request);
    setSelectedRequest(request);
    setViewModalVisible(true);
  };

  const handleEdit = (request: any) => {
    console.log('✏️ Editing request:', request);
    setEditingRequest(request);
    setFormVisible(true);
  };

  const handleDelete = async (requestId: string) => {
    try {
      console.log('🗑️ Deleting request ID:', requestId);
      setActionLoading(`delete-${requestId}`);
      
      const response = await apiService.deleteLeaveRequest(requestId);
      console.log('🗑️ Delete API Response:', response);
      
      if (response && response.success) {
        message.success('Leave request deleted successfully');
        fetchLeaveData();
      } else {
        console.error('❌ Delete failed:', response?.error);
        message.error(response?.error || 'Delete operation failed');
      }
    } catch (error) {
      console.error('💥 Delete error:', error);
      message.error('An error occurred while deleting');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = (request: any) => {
    console.log('✅ Preparing to approve request:', request);
    setSelectedRequest(request);
    setApprovalModalVisible(true);
  };

  const handleReject = (request: any) => {
    console.log('❌ Preparing to reject request:', request);
    setSelectedRequest(request);
    setRejectionModalVisible(true);
  };

  const confirmApproval = async () => {
    if (!selectedRequest) return;
    
    try {
      console.log('✅ Confirming approval for request ID:', selectedRequest.id);
      setActionLoading(`approve-${selectedRequest.id}`);
      
      const currentUser = JSON.parse(localStorage.getItem('hr_user') || '{}');
      const approvalData = {
        approved_by: currentUser.id || currentUser.employee_id || null,
        approved_date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        status: 'approved'
      };
      
      console.log('📤 Sending approval data:', approvalData);
      
      const response = await apiService.approveLeaveRequest(selectedRequest.request_id, approvalData);
      console.log('✅ Approval API Response:', response);
      
      if (response && response.success) {
        message.success('Leave request approved successfully');
        
        // Close modal first
        setApprovalModalVisible(false);
        setSelectedRequest(null);
        
        fetchLeaveData();
        
      } else {
        console.error('❌ Approval failed:', response?.error);
        message.error(response?.error || 'Approval operation failed');
      }
    } catch (error) {
      console.error('💥 Approval error:', error);
      message.error('An error occurred while approving');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmRejection = async (values: any) => {
    if (!selectedRequest) return;
    
    try {
      console.log('❌ Confirming rejection for request ID:', selectedRequest.id);
      console.log('📝 Rejection reason:', values.rejection_reason);
      setActionLoading(`reject-${selectedRequest.id}`);
      
      const rejectionData = {
        rejection_reason: values.rejection_reason,
        status: 'rejected'
      };
      
      console.log('📤 Sending rejection data:', rejectionData);
      
      const response = await apiService.rejectLeaveRequest(selectedRequest.request_id, rejectionData);
      console.log('❌ Rejection API Response:', response);
      
      if (response && response.success) {
        message.success('Leave request rejected');
        
        // Close modal and reset form
        setRejectionModalVisible(false);
        setSelectedRequest(null);
        rejectionForm.resetFields();
        
        // Refresh
        setTimeout(() => {
          fetchLeaveData();
        }, 500);
        
      } else {
        console.error('❌ Rejection failed:', response?.error);
        message.error(response?.error || 'Rejection operation failed');
      }
    } catch (error) {
      console.error('💥 Rejection error:', error);
      message.error('An error occurred while rejecting');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFormSuccess = () => {
    console.log('📝 Form submitted successfully');
    setFormVisible(false);
    setEditingRequest(null);
    message.success('Leave request saved successfully');
    
    // Force refresh immediately
    fetchLeaveData();
  };

  const handleFormCancel = () => {
    setFormVisible(false);
    setEditingRequest(null);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'error',
      'cancelled': 'default'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getLeaveTypeColor = (type: string) => {
    const colors = {
      'annual': 'blue',
      'sick': 'red',
      'maternity': 'pink',
      'paternity': 'cyan',
      'emergency': 'orange',
      'unpaid': 'default',
      'study': 'purple',
      'bereavement': 'magenta'
    };
    return colors[type as keyof typeof colors] || 'default';
  };

  const getLeaveTypeIcon = (type: string) => {
    const icons = {
      'annual': '🏖️',
      'sick': '🏥',
      'maternity': '🤱',
      'paternity': '👨‍👶',
      'emergency': '🚨',
      'unpaid': '💸',
      'study': '📚',
      'bereavement': '🖤'
    };
    return icons[type as keyof typeof icons] || '📅';
  };

  // Ensure requests is always an array
  const safeRequests = Array.isArray(requests) ? requests : [];
  
  console.log('🎯 Current requests in state:', safeRequests);
  console.log('📊 Total requests count:', safeRequests.length);
  
  const filteredRequests = safeRequests.filter(request => {
    const matchesSearch = request.employee_name?.toLowerCase().includes(searchText.toLowerCase()) ||
                         request.reason?.toLowerCase().includes(searchText.toLowerCase()) ||
                         request.request_id?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesLeaveType = leaveTypeFilter === 'all' || request.leave_type === leaveTypeFilter;
    
    return matchesSearch && matchesStatus && matchesLeaveType;
  });

  const requestColumns = [
    {
      title: 'Request Details',
      key: 'request',
      render: (record: any) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {record.employee_name || `Employee ID: ${record.employee_id}`}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: {record.request_id}
          </div>
          <Tag color={getLeaveTypeColor(record.leave_type)} style={{ marginTop: 4 }}>
            {getLeaveTypeIcon(record.leave_type)} {record.leave_type?.replace('_', ' ').toUpperCase()}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Leave Period',
      key: 'period',
      render: (record: any) => (
        <div>
          <div><CalendarOutlined /> {dayjs(record.start_date).format('MMM DD')} - {dayjs(record.end_date).format('MMM DD, YYYY')}</div>
          <div><ClockCircleOutlined /> {record.total_days} days</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Applied: {dayjs(record.applied_date || record.created_at).format('MMM DD, YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <div style={{ maxWidth: 200 }}>{text}</div>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: any) => (
        <div>
          <Tag color={getStatusColor(status)}>
            {status?.toUpperCase()}
          </Tag>
          {status === 'approved' && record.approved_date && (
            <div style={{ fontSize: '12px', color: '#52c41a' }}>
              Approved: {dayjs(record.approved_date).format('MMM DD')}
            </div>
          )}
          {status === 'rejected' && record.rejection_reason && (
            <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
              Rejected
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="text"
                  icon={<CheckCircleOutlined />}
                  style={{ color: '#52c41a' }}
                  onClick={() => handleApprove(record)}
                  loading={actionLoading === `approve-${record.id}`}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="text"
                  icon={<CloseCircleOutlined />}
                  danger
                  onClick={() => handleReject(record)}
                  loading={actionLoading === `reject-${record.id}`}
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Edit Request">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={record.status === 'approved'}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this request?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.request_id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete Request">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={actionLoading === `delete-${record.id}`}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const leaveTabItems = [
    {
      key: 'requests',
      label: <span><CalendarOutlined /> Leave Requests</span>,
      children: null, // will be set below
    },
    {
      key: 'balance',
      label: <span><BankOutlined /> Leave Balances</span>,
      children: (
        <Card title="Employee Leave Balances">
          <Table dataSource={balances} rowKey="id" pagination={{ pageSize: 15 }}
            columns={[
              { title: 'Employee', key: 'name', render: (_: any, r: any) => `${r.first_name || ''} ${r.last_name || ''}` },
              { title: 'Department', dataIndex: 'department' },
              { title: 'Leave Type', dataIndex: 'leave_type', render: (v: string) => <Tag color="blue">{v}</Tag> },
              { title: 'Entitlement', dataIndex: 'days_per_year', render: (v: number) => `${v} days` },
              { title: 'Used', dataIndex: 'used_balance', render: (v: any) => <Tag color="warning">{parseFloat(String(v||0)).toFixed(1)} days</Tag> },
              { title: 'Remaining', dataIndex: 'remaining', render: (v: any) => <Tag color={parseFloat(String(v||0)) > 5 ? 'success' : 'error'}>{parseFloat(String(v||0)).toFixed(1)} days</Tag> },
              { title: 'Year', dataIndex: 'year' },
            ]} />
        </Card>
      ),
    },
    {
      key: 'calendar',
      label: <span><CalendarOutlined /> Team Calendar</span>,
      children: (
        <Card title="Approved Leave Calendar" extra={
          <Input type="month" value={calendarMonth} onChange={e => setCalendarMonth(e.target.value)} style={{ width: 180 }} />
        }>
          <Table dataSource={calendar} rowKey="id" pagination={{ pageSize: 20 }}
            columns={[
              { title: 'Employee', dataIndex: 'employee_name' },
              { title: 'Department', dataIndex: 'department' },
              { title: 'Leave Type', dataIndex: 'leave_type', render: (v: string) => <Tag color="blue">{v}</Tag> },
              { title: 'From', dataIndex: 'start_date' },
              { title: 'To', dataIndex: 'end_date' },
              { title: 'Days', dataIndex: 'total_days' },
              { title: 'Status', dataIndex: 'status', render: () => <Tag color="success">APPROVED</Tag> },
            ]} />
          {calendar.length === 0 && <Empty description="No approved leave for this month" />}
        </Card>
      ),
    },
    {
      key: 'holidays',
      label: <span><GlobalOutlined /> Public Holidays</span>,
      children: (
        <Card title="Nigerian Public Holidays 2026" extra={
          <Button type="primary" size="small" onClick={() => setHolidayModal(true)}>Add Holiday</Button>
        }>
          <Table dataSource={holidays} rowKey="id" pagination={false}
            columns={[
              { title: 'Holiday', dataIndex: 'holiday_name' },
              { title: 'Date', dataIndex: 'holiday_date' },
              { title: 'Type', dataIndex: 'holiday_type', render: (v: string) => <Tag color={v==='national'?'red':v==='regional'?'orange':'blue'}>{v}</Tag> },
              { title: 'Day', dataIndex: 'holiday_date', key: 'day', render: (v: string) => new Date(v).toLocaleDateString('en-NG',{weekday:'long'}) },
            ]} />
        </Card>
      ),
    },
    {
      key: 'entitlements',
      label: <span><SettingOutlined /> Leave Policy</span>,
      children: (
        <Card title="Leave Entitlement Policy">
          <Table dataSource={entitlements} rowKey="id" pagination={false}
            columns={[
              { title: 'Leave Type', dataIndex: 'leave_type', render: (v: string) => <Tag color="blue">{v?.toUpperCase()}</Tag> },
              { title: 'Days Per Year', dataIndex: 'days_per_year', render: (v: number) => `${v} days` },
              { title: 'Carry-Over Limit', dataIndex: 'carry_over_limit', render: (v: number) => `${v} days` },
              { title: 'Min Notice', dataIndex: 'min_notice_days', render: (v: number) => `${v} day(s)` },
              { title: 'Max Consecutive', dataIndex: 'max_consecutive_days', render: (v: number) => `${v} days` },
              { title: 'Approval Required', dataIndex: 'requires_approval', render: (v: number) => v ? <Tag color="warning">Yes</Tag> : <Tag color="success">No</Tag> },
            ]} />
        </Card>
      ),
    },
  ];


  // Assign requests tab content
  const requestsContent = (
    <div style={{ padding: '0' }}>
          <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '24px' }}>
              <Title level={2}>
                <CalendarOutlined /> Leave Management
              </Title>
            </div>
      
            {/* Statistics Cards */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Requests"
                    value={safeRequests.length}
                    prefix={<CalendarOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Pending Approval"
                    value={safeRequests.filter(r => r.status === 'pending').length}
                    prefix={<Badge status="warning" />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Approved"
                    value={safeRequests.filter(r => r.status === 'approved').length}
                    prefix={<Badge status="success" />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Days Requested"
                    value={safeRequests.reduce((sum, r) => sum + parseFloat(String(r.total_days || 0)), 0)}
                    prefix={<ClockCircleOutlined />}
                  />
                </Card>
              </Col>
            </Row>
      
            {/* Filters and Actions */}
            <Card style={{ marginBottom: '24px' }}>
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Input
                    placeholder="Search by employee name, request ID, or reason..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 120 }}
                  >
                    <Option value="all">All Status</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="approved">Approved</Option>
                    <Option value="rejected">Rejected</Option>
                    <Option value="cancelled">Cancelled</Option>
                  </Select>
                </Col>
                <Col>
                  <Select
                    value={leaveTypeFilter}
                    onChange={setLeaveTypeFilter}
                    style={{ width: 140 }}
                  >
                    <Option value="all">All Types</Option>
                    <Option value="annual">Annual</Option>
                    <Option value="sick">Sick</Option>
                    <Option value="maternity">Maternity</Option>
                    <Option value="paternity">Paternity</Option>
                    <Option value="emergency">Emergency</Option>
                    <Option value="unpaid">Unpaid</Option>
                    <Option value="study">Study</Option>
                    <Option value="bereavement">Bereavement</Option>
                  </Select>
                </Col>
                <Col>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      fetchLeaveData();
                      message.info('Refreshing data...');
                    }}
                    loading={loading}
                    style={{ marginRight: 8 }}
                  >
                    Refresh
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setFormVisible(true)}
                    loading={loading}
                  >
                    New Request
                  </Button>
                </Col>
              </Row>
            </Card>
      
            {/* Main Table */}
            <Card title={`Leave Requests (${filteredRequests.length})`}>
              <Spin spinning={loading}>
                <Table
                  columns={requestColumns}
                  dataSource={filteredRequests}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
                  }}
                  scroll={{ x: 1200 }}
                  locale={{
                    emptyText: loading ? 'Loading...' : 'No leave requests found'
                  }}
                />
              </Spin>
            </Card>
      
            {/* Form Modal */}
            <LeaveRequestForm
              visible={formVisible}
              onCancel={handleFormCancel}
              onSuccess={handleFormSuccess}
              editingRequest={editingRequest}
            />
      
            {/* View Modal */}
            <Modal
              title="Leave Request Details"
              open={viewModalVisible}
              onCancel={() => setViewModalVisible(false)}
              footer={[
                <Button key="close" onClick={() => setViewModalVisible(false)}>
                  Close
                </Button>
              ]}
              width={700}
            >
              {selectedRequest && (
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Request ID" span={2}>
                    <strong>{selectedRequest.request_id}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Employee">
                    {selectedRequest.employee_name || `ID: ${selectedRequest.employee_id}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Leave Type">
                    <Tag color={getLeaveTypeColor(selectedRequest.leave_type)}>
                      {getLeaveTypeIcon(selectedRequest.leave_type)} {selectedRequest.leave_type?.replace('_', ' ').toUpperCase()}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Start Date">
                    {dayjs(selectedRequest.start_date).format('MMMM DD, YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item label="End Date">
                    {dayjs(selectedRequest.end_date).format('MMMM DD, YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Days">
                    <strong>{selectedRequest.total_days} days</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(selectedRequest.status)}>
                      {selectedRequest.status?.toUpperCase()}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Applied Date" span={2}>
                    {dayjs(selectedRequest.applied_date || selectedRequest.created_at).format('MMMM DD, YYYY HH:mm')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reason" span={2}>
                    {selectedRequest.reason}
                  </Descriptions.Item>
                  {selectedRequest.handover_notes && (
                    <Descriptions.Item label="Handover Notes" span={2}>
                      {selectedRequest.handover_notes}
                    </Descriptions.Item>
                  )}
                  {selectedRequest.covering_employee_id && (
                    <Descriptions.Item label="Covering Employee" span={2}>
                      Employee ID: {selectedRequest.covering_employee_id}
                    </Descriptions.Item>
                  )}
                  {selectedRequest.approved_by && (
                    <Descriptions.Item label="Approved By" span={2}>
                      User ID: {selectedRequest.approved_by} on {dayjs(selectedRequest.approved_date).format('MMMM DD, YYYY HH:mm')}
                    </Descriptions.Item>
                  )}
                  {selectedRequest.rejection_reason && (
                    <Descriptions.Item label="Rejection Reason" span={2}>
                      <div style={{ color: '#ff4d4f' }}>{selectedRequest.rejection_reason}</div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              )}
            </Modal>
      
            {/* Approval Modal */}
            <Modal
              title="Approve Leave Request"
              open={approvalModalVisible}
              onOk={confirmApproval}
              onCancel={() => {
                setApprovalModalVisible(false);
                setSelectedRequest(null);
              }}
              okText="Approve"
              cancelText="Cancel"
              confirmLoading={actionLoading?.startsWith('approve')}
            >
              <p>Are you sure you want to approve this leave request?</p>
              {selectedRequest && (
                <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px', marginTop: '12px' }}>
                  <strong>{selectedRequest.employee_name || `Employee ID: ${selectedRequest.employee_id}`}</strong><br />
                  <strong>Type:</strong> {selectedRequest.leave_type}<br />
                  <strong>Period:</strong> {dayjs(selectedRequest.start_date).format('MMM DD')} - {dayjs(selectedRequest.end_date).format('MMM DD, YYYY')}<br />
                  <strong>Days:</strong> {selectedRequest.total_days}<br />
                  <strong>Reason:</strong> {selectedRequest.reason}
                </div>
              )}
            </Modal>
      
            {/* Rejection Modal */}
            <Modal
              title="Reject Leave Request"
              open={rejectionModalVisible}
              onOk={() => rejectionForm.submit()}
              onCancel={() => {
                setRejectionModalVisible(false);
                setSelectedRequest(null);
                rejectionForm.resetFields();
              }}
              okText="Reject"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              confirmLoading={actionLoading?.startsWith('reject')}
            >
              <Form
                form={rejectionForm}
                onFinish={confirmRejection}
                layout="vertical"
              >
                <p>Please provide a reason for rejecting this leave request:</p>
                <Form.Item
                  name="rejection_reason"
                  rules={[{ required: true, message: 'Please provide rejection reason' }]}
                >
                  <TextArea rows={4} placeholder="Enter reason for rejection..." />
                </Form.Item>
              </Form>
            </Modal>
          </div>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <Tabs
        activeKey={leaveTab}
        onChange={setLeaveTab}
        items={[
          { key: 'requests', label: <span><CalendarOutlined /> Leave Requests</span>, children: requestsContent },
          ...leaveTabItems.slice(1),
        ]}
      />

      {/* Holiday Modal */}
      <Modal title="Add Public Holiday" open={holidayModal} onCancel={() => setHolidayModal(false)} footer={null} width={400}>
        <Form form={holidayForm} layout="vertical" onFinish={async (values: any) => {
          const r = await apiService.addPublicHoliday({ ...values, year: new Date().getFullYear() });
          if (r.success) { message.success('Holiday added'); setHolidayModal(false); holidayForm.resetFields(); fetchLeaveExtras(); }
          else message.error(r.error || 'Failed');
        }}>
          <Form.Item name="holiday_name" label="Holiday Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="holiday_date" label="Date" rules={[{ required: true }]}><Input type="date" /></Form.Item>
          <Form.Item name="holiday_type" label="Type" initialValue="national"><Select><Select.Option value="national">National</Select.Option><Select.Option value="regional">Regional</Select.Option><Select.Option value="company">Company</Select.Option></Select></Form.Item>
          <Space><Button type="primary" htmlType="submit">Add</Button><Button onClick={() => setHolidayModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};

export default LeaveManagement;
